const STATUS_PRIORITY = {
  pending: 0,
  rejected: 1,
  approved: 2,
  withdrawn_by_user: 3,
};

/**
 * Collapse join-request rows into per-club status, request id, and cooldown info.
 * Pending wins over rejected/withdrawn when a player has more than one row for a club.
 */
export function mapJoinRequestsByClub(rows) {
  const requests = {};
  const requestIds = {};
  const cooldowns = {};

  (rows || []).forEach((request) => {
    const clubId = request.club_id;
    if (!clubId) return;

    const nextStatus = request.status;
    const nextPri = STATUS_PRIORITY[nextStatus] ?? 9;
    const existingPri =
      requests[clubId] != null ? (STATUS_PRIORITY[requests[clubId]] ?? 9) : 9;

    if (nextPri < existingPri) {
      requests[clubId] = nextStatus;
      requestIds[clubId] = request.id ?? request.request_id;
    }

    if (request.cooldown_until || request.is_blocked) {
      const cooldownUntil = request.cooldown_until ? new Date(request.cooldown_until) : null;
      const isActive = cooldownUntil && cooldownUntil > new Date();

      if (isActive || request.is_blocked) {
        const existing = cooldowns[clubId];
        const existingUntil = existing?.cooldown_until
          ? new Date(existing.cooldown_until)
          : null;
        const shouldReplace =
          !existing ||
          Boolean(request.is_blocked) ||
          (cooldownUntil && (!existingUntil || cooldownUntil > existingUntil));

        if (shouldReplace) {
          cooldowns[clubId] = {
            cooldown_until: request.cooldown_until,
            is_blocked: request.is_blocked,
            rejection_message: request.rejection_message,
          };
        }
      }
    }
  });

  return { requests, requestIds, cooldowns };
}

/**
 * Discover-card membership status for a club the user does not already belong to.
 * Join-request pending takes priority over an account-link request.
 */
export function getDiscoverClubStatus({
  isLoggedIn,
  membership,
  joinRequest,
  emailPending = false,
  cooldownInfo,
} = {}) {
  if (membership?.membershipStatus === 'inactive' || membership?.status === 'inactive') {
    return {
      status: 'inactive',
      message: membership?.reactivationRequestedAt
        ? 'Reactivation requested'
        : 'Membership inactive',
      reactivationRequested: Boolean(membership?.reactivationRequestedAt),
    };
  }

  if (membership?.isActive) {
    return { status: 'member', message: 'Member' };
  }

  if (joinRequest === 'pending') {
    return { status: 'pending', message: 'Request Pending' };
  }

  if (joinRequest === 'rejected' || joinRequest === 'denied') {
    return { status: 'rejected', message: 'Request Rejected' };
  }

  if (cooldownInfo) {
    const cooldownUntil = cooldownInfo.cooldown_until
      ? new Date(cooldownInfo.cooldown_until)
      : null;
    const isActive = cooldownUntil && cooldownUntil > new Date();

    if (cooldownInfo.is_blocked) {
      return {
        status: 'blocked',
        message: 'Blocked from joining',
        cooldownInfo,
      };
    }

    if (isActive) {
      return {
        status: 'cooldown',
        message: 'Request on cooldown',
        cooldownInfo,
      };
    }
  }

  if (emailPending) {
    return { status: 'link_pending', message: 'Account link requested' };
  }

  if (isLoggedIn) {
    return { status: 'can_join', message: 'Not a member' };
  }

  return null;
}

export function isJoinBlockedStatus(status) {
  return (
    status === 'pending' ||
    status === 'link_pending' ||
    status === 'rejected' ||
    status === 'cooldown' ||
    status === 'blocked' ||
    status === 'member' ||
    status === 'inactive'
  );
}
